import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  useTheme,
  useMediaQuery,
  Slide,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconReusable from '../../../components/IconReusable';

// Helper function to format time in MM:SS format
const formatTime = (timeInSeconds) => {
  if (!timeInSeconds || isNaN(timeInSeconds)) return '0:00';
  
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const VideoModal = ({
  open,
  onClose,
  title = '',
  subtitle = '',
  children,
  maxWidth = '400px',
  topDesktop = '50%',
  topMobile = '0%',
  scrollable = true,
  maxHeight = '0%',
  desktopHeight = 'auto',
  pt = 2,
  zIndex = 2,
  showHeader = true,
  currentTime = 0,
  duration = 0,
  onSeek,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [progress, setProgress] = React.useState(0)
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 100);
      });
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);
  const modalContent = (
    
    <Box
      sx={{
        position: 'absolute',
        top: isMobile ? topMobile : topDesktop,
        left: isMobile ? 0 : '50%',
        transform: isMobile ? 'none' : 'translate(-50%, -50%)',
        width: isMobile ? '100%' : maxWidth,
        height: isMobile ? `calc(100vh - ${maxHeight})` : 'auto',
        maxHeight: isMobile ? `calc(100vh - ${maxHeight})` : '90vh',
        backgroundColor: !isMobile ? '#ffffffd1' : 'transparent',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        borderTopLeftRadius: isMobile ? 0 : 16,
        borderTopRightRadius: isMobile ? 0 : 16,
        borderBottomLeftRadius: isMobile ? 0 : 16,  
        borderBottomRightRadius: isMobile ? 0 : 16,
        boxShadow: 24,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
       <Box
          sx={{
            width: '100%',
            height: { xs: '100vh', md: 'auto' },
            position: 'absolute',
            top: -1,
            left: 0,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgb(0 0 0) 0%, 0%, rgba(255,255,255,0.05) 100%)',
            backgroundBlendMode: 'overlay',
            zIndex: 0,
            opacity: 0.6,
            // border: '.5px solid rgba(255,255,255, 0.2)',
          }}
        />
        <Box
          sx={{
            width:'100%',
            height: { xs: '100vh', md: 'auto' },
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: 2,
            backgroundColor: !isMobile ? 'rgba(0, 0, 0, 0)' : '#ffffff7a',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            boxShadow: `
              inset 1px 1px 4px 0px rgba(255, 255, 255, 0.6),
              inset 1px 1px 0px 0px rgba(255, 255, 255, 1),
              inset 10px 0px 20px 0px rgba(0, 0, 0, 0.05),
              inset 1px 4px 40px 1px rgba(255, 255, 255, 1),
              inset 0px 2px 16px 1px rgba(255, 255, 255, 1),
              inset 0px -2px 0px 0px rgba(255, 255, 255, 1),
              inset -1px -4px 16px 0px rgba(255, 255, 255, 0.8)
            `,
            zIndex: 1,
            
          }}
        />
     
      {showHeader &&
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            py: 1,
            px: { md: 2, xs: 2 },
            display: 'flex',
            flexDirection: 'column',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              // mb: duration > 0 ? 2 : 0,
            }}
          >
            <LinearProgress
              variant="determinate"
              value={duration > 0 ? (currentTime / duration) * 100 : 0}
              sx={{
                width: '90%',
                height: '6px',
                borderRadius: '3px',
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                cursor: onSeek ? 'pointer' : 'default',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#FFF',
                  borderRadius: '3px',
                },
              }}
              onClick={onSeek ? (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const percentage = clickX / rect.width;
                const seekTime = percentage * duration;
                onSeek(seekTime);
              } : undefined}
            />
            {onClose && (
              <Box sx={{ cursor: 'pointer', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                <Box sx={{ cursor: 'pointer', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconReusable name="Close" onClick={onClose} color='#333'  width={'24px'} height={'24px'}/>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      }
      <Box
        sx={{
          px: { md: 2, xs: 2 },
          pb: { xs: 0, md: 2},
          overflowY: scrollable ? 'auto' : 'unset',
          flex: 1,
          position: 'relative',
          zIndex: zIndex,
        }}
      > 
        {children}
      </Box>
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose}>
      {isMobile ? (
        <Slide direction="up" in={open} mountOnEnter unmountOnExit timeout={{ enter: 500, exit: 300 }}>
          {modalContent}
        </Slide>
      ) : (
        modalContent
      )}
    </Modal>
  );
};

export default VideoModal;
